import { useState, useEffect, useCallback } from 'react';
import { Link, useLocation as useRouterLocation } from 'react-router-dom';
import { Tag, Calendar, User, Search, LogOut, Map, Store, Bell, Sparkles, BellOff, Moon, Sun, Download, X, Share, Smartphone, MessageSquare, Layers, Fingerprint, WifiOff, CloudOff } from 'lucide-react';import { Button } from './ui/button';
import { Avatar, AvatarImage, AvatarFallback } from './ui/avatar';
import { Badge } from './ui/badge';
import { useAuth } from '../contexts/AuthContext';
import { useLocation } from '../contexts/LocationContext';
import { useTheme } from '../contexts/ThemeContext';
import { usePushNotifications } from '../hooks/usePushNotifications';
import AuthModal from './AuthModal';
import LocationPickerModal from './LocationPickerModal';
import FoodCardIcon from './FoodCardIcon';

/* Compact chef hat icon for Feed nav — matches lucide stroke style */
const FQFeedIcon = ({ className = '' }) => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" className={className}>
    <path d="M6 19h12" />
    <path d="M6 16h12v3H6z" />
    <path d="M6 16c-2 0-3.5-1.5-3.5-3.5S4 9 6 9c0-2.5 2.5-4.5 6-4.5s6 2 6 4.5c2 0 3.5 1.5 3.5 3.5S20 16 18 16" />
  </svg>
);

const API_URL = process.env.REACT_APP_BACKEND_URL;

const Navigation = () => {
  const location = useRouterLocation();
  const { user, loading, logout } = useAuth();
  const { locationString } = useLocation();
  const { dark, toggleTheme } = useTheme();
  const { permission, subscribed, requestPermission, unsubscribe, isSupported } = usePushNotifications(locationString);
  const [showAuthModal, setShowAuthModal] = useState(false);
  const [showUserMenu, setShowUserMenu] = useState(false);
  const [showLocationModal, setShowLocationModal] = useState(false);
  const [showNotifications, setShowNotifications] = useState(false);
  const [notifications, setNotifications] = useState([]);
  const [unreadCount, setUnreadCount] = useState(0);
  const [deferredPrompt, setDeferredPrompt] = useState(null);
  const [showInstallModal, setShowInstallModal] = useState(false);
  const [isInstalled, setIsInstalled] = useState(false);
  const [isOnline, setIsOnline] = useState(navigator.onLine);

  // Online/offline detection
  useEffect(() => {
    const goOnline = () => setIsOnline(true);
    const goOffline = () => setIsOnline(false);
    window.addEventListener('online', goOnline);
    window.addEventListener('offline', goOffline);
    return () => {
      window.removeEventListener('online', goOnline);
      window.removeEventListener('offline', goOffline);
    };
  }, []);

  // Detect platform
  const isIOS = /iphone|ipad|ipod/.test(navigator.userAgent.toLowerCase());
  const isSafari = /safari/.test(navigator.userAgent.toLowerCase()) && !/chrome/.test(navigator.userAgent.toLowerCase());
  const isAndroid = /android/.test(navigator.userAgent.toLowerCase());

  // Detect if already installed
  useEffect(() => {
    const standalone = window.matchMedia('(display-mode: standalone)').matches || window.navigator.standalone;
    if (standalone) setIsInstalled(true);
  }, []);

  // Capture install prompt
  useEffect(() => {
    const handler = (e) => { e.preventDefault(); setDeferredPrompt(e); };
    window.addEventListener('beforeinstallprompt', handler);
    window.addEventListener('appinstalled', () => setIsInstalled(true));
    return () => window.removeEventListener('beforeinstallprompt', handler);
  }, []);

  const handleInstallClick = useCallback(async () => {
    // Direct install if browser supports it
    if (deferredPrompt) {
      deferredPrompt.prompt();
      const { outcome } = await deferredPrompt.userChoice;
      if (outcome === 'accepted') setIsInstalled(true);
      setDeferredPrompt(null);
      return;
    }
    // iOS / unsupported — show instructions only as fallback
    if (isIOS || !('BeforeInstallPromptEvent' in window || deferredPrompt)) {
      setShowInstallModal(true);
    }
  }, [deferredPrompt, isIOS]);

  // Poll unread count every 30s
  useEffect(() => {
    if (!user) { setUnreadCount(0); return; }
    const fetchUnread = () => {
      fetch(`${API_URL}/api/notifications/unread-count`, { credentials: 'include' })
        .then(r => r.ok ? r.json() : {})
        .then(d => setUnreadCount(d.unread_count || 0))
        .catch(() => {});
    };
    fetchUnread();
    const t = setInterval(fetchUnread, 30000);
    return () => clearInterval(t);
  }, [user]);

  const openNotifications = async () => {
    setShowNotifications(v => !v);
    if (!showNotifications && user) {
      const res = await fetch(`${API_URL}/api/notifications?limit=10`, { credentials: 'include' });
      if (res.ok) { const d = await res.json(); setNotifications(d.notifications || []); }
      await fetch(`${API_URL}/api/notifications/read`, { method: 'PUT', credentials: 'include' });
      setUnreadCount(0);
    }
  };

  const NOTIF_ICONS = { follow: '👤', like: '❤️', comment: '💬', review: '⭐', points: '✨' };

  const navItems = [
    { path: '/foodie',   label: 'Feed',      icon: FQFeedIcon },
    { path: '/profile',  label: 'Profile',   icon: User     },
    { path: '/surprise-me', label: 'Surprise Me', icon: Sparkles, gradient: true },
  ];

  return (
    <>
      {/* Offline indicator banner */}
      {!isOnline && (
        <div className="fixed top-0 left-0 right-0 z-[60] bg-amber-500 text-white text-center py-1.5 text-xs font-semibold flex items-center justify-center gap-2" data-testid="offline-banner">
          <WifiOff className="w-3.5 h-3.5" /> You're offline — changes will sync when you reconnect
        </div>
      )}
      <nav className={`bg-white shadow-sm sticky top-0 z-50 ${!isOnline ? 'mt-8' : ''}`}>
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center h-16">
            {/* Logo */}
            <Link to="/" data-testid="nav-logo-link" className="flex items-center gap-1.5">
              <img
                src="/icons/foodquest-logo-transparent.png"
                alt="FoodQuest"
                className="h-10 w-auto object-contain"
                data-testid="nav-logo-image"
              />
              <span className="text-2xl font-bold bg-gradient-to-r from-[#F4C430] via-[#FF8C42] to-[#FF6B35] bg-clip-text text-transparent">
                FoodQuest
              </span>
            </Link>

            {/* Desktop Nav */}
            <div className="hidden md:flex items-center space-x-1">
              {navItems.map((item) => {
                const Icon = item.icon;
                const isActive = location.pathname === item.path;
                
                if (item.gradient) {
                  return (
                    <Link key={item.path} to={item.path}>
                      <Button
                        variant="ghost"
                        className={`flex items-center space-x-2 transition-colors duration-200 ${
                          isActive 
                            ? 'bg-gradient-to-r from-[#F4C430] to-[#FF6B35] text-white' 
                            : 'text-[#FF6B35] hover:bg-[#FFF5F0]'
                        }`}
                      >
                        <Icon className="w-4 h-4" />
                        <span className="font-semibold">{item.label}</span>
                      </Button>
                    </Link>
                  );
                }
                
                return (
                  <Link key={item.path} to={item.path}>
                    <Button
                      variant="ghost"
                      className={`flex items-center space-x-2 transition-colors duration-200 ${
                        isActive ? 'text-[#FF6B35] bg-[#FFF5F0]' : 'text-gray-600 hover:text-[#FF6B35] hover:bg-gray-50'
                      }`}
                    >
                      <Icon className="w-4 h-4" />
                      <span className={isActive ? 'font-semibold' : 'font-normal'}>{item.label}</span>
                    </Button>
                  </Link>
                );
              })}

              {/* App Download */}
              {!isInstalled && (
                <button
                  data-testid="download-app-btn"
                  onClick={handleInstallClick}
                  className="flex items-center gap-1.5 px-3 py-1.5 text-sm font-medium text-[#FF6B35] hover:bg-[#FFF5F0] rounded-2xl transition-colors"
                  title="Install App"
                >
                  <Smartphone className="w-4 h-4" />
                  <span>App</span>
                </button>
              )}

              {/* Dark mode toggle */}
              <button
                data-testid="dark-mode-toggle"
                onClick={toggleTheme}
                className="p-2 text-gray-500 hover:text-[#FF6B35] hover:bg-[#FFF5F0] dark:hover:bg-gray-800 rounded-2xl transition-colors"
                title={dark ? 'Light mode' : 'Dark mode'}
              >
                {dark ? <Sun className="w-4 h-4" /> : <Moon className="w-4 h-4" />}
              </button>

              {/* Unified Notification Bell — tap to toggle on/off */}
              {user && (
                <div className="relative">
                  <button
                    data-testid="notifications-btn"
                    onClick={openNotifications}
                    className={`p-2 rounded-2xl transition-colors relative ${subscribed ? 'text-[#FF6B35] bg-[#FFF5F0]' : 'text-gray-500 hover:text-[#FF6B35] hover:bg-[#FFF5F0]'}`}
                    title={subscribed ? 'Notifications on' : 'Notifications off'}
                  >
                    {subscribed ? <Bell className="w-4 h-4" /> : <BellOff className="w-4 h-4" />}
                    {unreadCount > 0 && (
                      <span className="absolute -top-0.5 -right-0.5 w-4 h-4 bg-red-500 text-white text-[10px] font-bold rounded-full flex items-center justify-center">
                        {unreadCount > 9 ? '9+' : unreadCount}
                      </span>
                    )}
                  </button>

                  {showNotifications && (
                    <div className="absolute right-0 top-full mt-2 bg-white rounded-2xl shadow-2xl border border-gray-100 w-80 z-50 overflow-hidden">
                      <div className="px-4 py-3 border-b border-gray-100 flex items-center justify-between">
                        <h3 className="font-semibold text-gray-900 text-sm">Notifications</h3>
                        <div className="flex items-center gap-2">
                          {isSupported && (
                            <button
                              data-testid="toggle-notifications-btn"
                              onClick={(e) => { e.stopPropagation(); subscribed ? unsubscribe() : requestPermission(); }}
                              className={`text-xs px-2 py-1 rounded-full transition-colors ${subscribed ? 'bg-[#FFF5F0] text-[#FF6B35]' : 'bg-gray-100 text-gray-500'}`}
                            >
                              {subscribed ? 'On' : 'Off'}
                            </button>
                          )}
                          <button onClick={() => setShowNotifications(false)} className="text-gray-400 hover:text-gray-600 text-xs">Close</button>
                        </div>
                      </div>
                      <div className="max-h-80 overflow-y-auto">
                        {notifications.length === 0 ? (
                          <p className="text-center text-sm text-gray-500 py-8">No notifications yet</p>
                        ) : notifications.map((n) => (
                          <div key={n.notif_id} className={`px-4 py-3 border-b border-gray-50 last:border-0 ${n.read ? 'bg-white' : 'bg-[#FFF5F0]'}`}>
                            <div className="flex items-start gap-3">
                              <span className="text-lg flex-shrink-0">{NOTIF_ICONS[n.type] || '🔔'}</span>
                              <div>
                                <p className="text-sm text-gray-800">{n.message}</p>
                                <p className="text-xs text-gray-400 mt-0.5">{new Date(n.created_at).toLocaleDateString()}</p>
                              </div>
                            </div>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}
                </div>
              )}
              {!loading && (
                user ? (
                  <div className="relative ml-2">
                    <button
                      data-testid="logout-btn"
                      onClick={() => logout()}
                      className="text-sm text-gray-500 hover:text-red-500 px-3 py-1.5 rounded-lg hover:bg-gray-50 transition-colors flex items-center gap-1.5"
                    >
                      <LogOut className="w-4 h-4" />
                      <span className="hidden lg:inline">Sign Out</span>
                    </button>
                  </div>
                ) : (
                  <Button
                    data-testid="signin-btn"
                    onClick={() => setShowAuthModal(true)}
                    className="ml-2 bg-gradient-to-r from-[#F4C430] via-[#FF8C42] to-[#FF6B35] text-white hover:opacity-90"
                    size="sm"
                  >
                    Sign In
                  </Button>
                )
              )}
            </div>

            {/* Mobile right */}
            <div className="md:hidden flex items-center gap-2">
              {!isInstalled && (
                <button
                  data-testid="mobile-download-app-btn"
                  onClick={handleInstallClick}
                  className="p-2 text-[#FF6B35] hover:bg-[#FFF5F0] rounded-xl transition-colors"
                  title="Install App"
                >
                  <Smartphone className="w-5 h-5" />
                </button>
              )}
              {!user && (
                <Button size="sm" onClick={() => setShowAuthModal(true)} className="bg-gradient-to-r from-[#F4C430] to-[#FF6B35] text-white text-xs px-3">
                  Sign In
                </Button>
              )}
            </div>
          </div>
        </div>

      </nav>

      {showAuthModal && <AuthModal onClose={() => setShowAuthModal(false)} />}
      {showLocationModal && <LocationPickerModal onClose={() => setShowLocationModal(false)} />}

      {/* Install App Modal */}
      {showInstallModal && (
        <div className="fixed inset-0 z-[10060] bg-black/40 flex items-center justify-center px-4" onClick={() => setShowInstallModal(false)}>
          <div className="w-full max-w-sm bg-white rounded-2xl overflow-hidden shadow-2xl" onClick={(e) => e.stopPropagation()}>
            <div className="bg-gradient-to-r from-[#F4C430] via-[#FF8C42] to-[#FF6B35] px-5 py-4 flex items-center justify-between">
              <div className="flex items-center gap-3">
                <img src="/icons/foodcard-color.png" alt="FoodQuest" className="w-12 h-12 rounded-xl bg-white/20 p-1" />
                <div>
                  <h3 className="text-white font-bold text-base">Get FoodQuest</h3>
                  <p className="text-white/75 text-xs">Install on your device</p>
                </div>
              </div>
              <button data-testid="install-modal-close" onClick={() => setShowInstallModal(false)} className="text-white/70 hover:text-white p-1">
                <X className="w-5 h-5" />
              </button>
            </div>

            <div className="p-5 space-y-4">
              {/* Benefits */}
              <div className="flex items-center gap-3 text-sm text-gray-600">
                <Smartphone className="w-5 h-5 text-[#FF6B35] flex-shrink-0" />
                <span>Works on phone, tablet &amp; laptop &mdash; no app store needed</span>
              </div>

              {isIOS ? (
                <div className="space-y-3">
                  <p className="text-sm font-medium text-gray-800">Install on iPhone / iPad:</p>
                  <div className="space-y-2.5">
                    <div className="flex items-center gap-3">
                      <span className="w-6 h-6 bg-[#FF6B35] text-white rounded-full flex items-center justify-center text-xs font-bold flex-shrink-0">1</span>
                      <span className="text-sm text-gray-600">Tap the <Share className="w-4 h-4 inline text-blue-500" /> <strong>Share</strong> button</span>
                    </div>
                    <div className="flex items-center gap-3">
                      <span className="w-6 h-6 bg-[#FF6B35] text-white rounded-full flex items-center justify-center text-xs font-bold flex-shrink-0">2</span>
                      <span className="text-sm text-gray-600">Scroll and tap <strong>"Add to Home Screen"</strong></span>
                    </div>
                    <div className="flex items-center gap-3">
                      <span className="w-6 h-6 bg-[#FF6B35] text-white rounded-full flex items-center justify-center text-xs font-bold flex-shrink-0">3</span>
                      <span className="text-sm text-gray-600">Tap <strong>"Add"</strong></span>
                    </div>
                  </div>
                  <button onClick={() => setShowInstallModal(false)} className="w-full py-3 rounded-xl bg-gray-100 text-gray-700 text-sm font-medium hover:bg-gray-200 transition-colors">
                    Got it
                  </button>
                </div>
              ) : deferredPrompt ? (
                <button
                  data-testid="install-app-confirm-btn"
                  onClick={handleInstallClick}
                  className="w-full py-3 rounded-xl bg-[#FF6B35] text-white text-sm font-semibold hover:bg-[#E85D04] active:scale-[0.98] transition-all flex items-center justify-center gap-2"
                >
                  <Download className="w-4 h-4" />
                  Install Now
                </button>
              ) : (
                <div className="space-y-3">
                  <p className="text-sm font-medium text-gray-800">
                    {isAndroid ? 'Install on Android:' : 'Install on your device:'}
                  </p>
                  <div className="space-y-2.5">
                    <div className="flex items-center gap-3">
                      <span className="w-6 h-6 bg-[#FF6B35] text-white rounded-full flex items-center justify-center text-xs font-bold flex-shrink-0">1</span>
                      <span className="text-sm text-gray-600">Open in <strong>Chrome</strong> or <strong>Edge</strong></span>
                    </div>
                    <div className="flex items-center gap-3">
                      <span className="w-6 h-6 bg-[#FF6B35] text-white rounded-full flex items-center justify-center text-xs font-bold flex-shrink-0">2</span>
                      <span className="text-sm text-gray-600">Tap the <strong>menu (&#8942;)</strong> or address bar install icon</span>
                    </div>
                    <div className="flex items-center gap-3">
                      <span className="w-6 h-6 bg-[#FF6B35] text-white rounded-full flex items-center justify-center text-xs font-bold flex-shrink-0">3</span>
                      <span className="text-sm text-gray-600">Select <strong>"Install app"</strong> or <strong>"Add to Home Screen"</strong></span>
                    </div>
                  </div>
                  <button onClick={() => setShowInstallModal(false)} className="w-full py-3 rounded-xl bg-gray-100 text-gray-700 text-sm font-medium hover:bg-gray-200 transition-colors">
                    Got it
                  </button>
                </div>
              )}
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default Navigation;
